import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import GoogleOneTap from '../components/auth/GoogleOneTap';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  ChartBarIcon,
  ClockIcon,
  RocketLaunchIcon,
  BookOpenIcon,
  AcademicCapIcon,
  CogIcon,
  ArrowRightIcon,
  LightBulbIcon,
  TrophyIcon,
  StarIcon,
  UserGroupIcon,
  DeviceTabletIcon,
  CpuChipIcon,
  PuzzlePieceIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

const MainLanding: React.FC = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  // SEO metadata
  const pageTitle = 'Upmindy | Plataforma de Estudos';
  const pageDescription = 'Estude com flashcards e repetição espaçada, pratique com quizzes e jogos de associação e organize seus estudos por módulos no Upmindy.';
  const pageUrl = 'https://upmindy.com';
  const pageImage = 'https://upmindy.com/og-image.png'; // You'll need to add this image
  const keywords = 'flashcards, repetição espaçada, aprendizado, memorização, estudo, programação, Node.js, estruturas de dados, inglês, quiz, spaced repetition, anki';

  const platformFeatures = [
    {
      icon: <BookOpenIcon className="h-12 w-12 text-purple-500" />,
      title: 'Várias formas de estudar',
      description: 'Revise com flashcards, teste seus conhecimentos com quizzes e pratique com jogos de associação'
    },
    {
      icon: <CpuChipIcon className="h-12 w-12 text-blue-500" />,
      title: 'Repetição Espaçada',
      description: 'Sistema inteligente baseado em algoritmos científicos para otimizar sua memorização'
    },
    {
      icon: <ChartBarIcon className="h-12 w-12 text-blue-500" />,
      title: 'Acompanhamento de Progresso',
      description: 'Relatórios detalhados do seu desenvolvimento e estatísticas de aprendizado'
    },
    {
      icon: <ClockIcon className="h-12 w-12 text-blue-500" />,
      title: 'Estudo Otimizado',
      description: 'Algoritmo inteligente que determina o momento ideal para revisar cada conceito'
    },
    {
      icon: <BookOpenIcon className="h-12 w-12 text-blue-500" />,
      title: 'Múltiplos Temas',
      description: 'Biblioteca crescente de ferramentas especializadas em diferentes áreas do conhecimento'
    },
    {
      icon: <DeviceTabletIcon className="h-12 w-12 text-blue-500" />,
      title: 'Multiplataforma',
      description: 'Acesse seus estudos em qualquer dispositivo, sincronizado na nuvem'
    },
    {
      icon: <CogIcon className="h-12 w-12 text-blue-500" />,
      title: 'Personalização',
      description: 'Configure intervalos, dificuldades e preferências conforme seu ritmo'
    }
  ];

  const studyBenefits = [
    {
      icon: <LightBulbIcon className="h-12 w-12 text-green-500" />,
      title: 'Aprendizado Científico',
      description: 'Baseado em pesquisas sobre memória e cognição para máxima eficiência'
    },
    {
      icon: <TrophyIcon className="h-12 w-12 text-green-500" />,
      title: 'Gamificação',
      description: 'Sistema de conquistas e estatísticas para manter sua motivação alta'
    },
    {
      icon: <UserGroupIcon className="h-12 w-12 text-green-500" />,
      title: 'Comunidade Ativa',
      description: 'Conecte-se com outros estudantes e compartilhe conhecimento'
    },
    {
      icon: <RocketLaunchIcon className="h-12 w-12 text-green-500" />,
      title: 'Resultados Rápidos',
      description: 'Veja melhorias na retenção de conhecimento em apenas algumas sessões'
    }
  ];

  const testimonials = [
    {
      name: 'Maria Silva',
      role: 'Estudante de Medicina',
      content: 'O Upmindy revolucionou minha forma de estudar anatomia. Em 3 meses memorizei mais do que consegui em um ano inteiro.',
      rating: 5
    },
    {
      name: 'João Santos',
      role: 'Concurseiro',
      content: 'A repetição espaçada realmente funciona! Consegui fixar matérias complexas de direito muito mais facilmente.',
      rating: 5
    },
    {
      name: 'Ana Oliveira',
      role: 'Estudante de Idiomas',
      content: 'Interface limpa, conteúdo de qualidade e método eficaz. Aprendi inglês mais rápido do que imaginava possível.',
      rating: 5
    },
    {
      name: 'Carlos Mendes',
      role: 'Desenvolvedor de Software',
      content: 'Usei o Upmindy para revisar estruturas de dados e algoritmos antes de entrevistas. Fiz um progresso enorme em poucas semanas.',
      rating: 5
    },
    {
      name: 'Fernanda Costa',
      role: 'Estudante de Enfermagem',
      content: 'Os flashcards com repetição espaçada me ajudaram a fixar termos técnicos que antes eu esquecia rapidamente.',
      rating: 5
    },
    {
      name: 'Rafael Almeida',
      role: 'Concurseiro',
      content: 'Consigo estudar em qualquer lugar pelo celular e o sistema sempre me mostra o que preciso revisar. Recomendo muito!',
      rating: 5
    }
  ];

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${pageUrl}/#website`,
        "url": pageUrl,
        "name": "Upmindy",
        "description": pageDescription,
        "publisher": {
          "@id": `${pageUrl}/#organization`
        },
        "inLanguage": "pt-BR"
      },
      {
        "@type": "Organization",
        "@id": `${pageUrl}/#organization`,
        "name": "Upmindy",
        "url": pageUrl,
        "logo": {
          "@type": "ImageObject",
          "url": `${pageUrl}/favicon-192x192.png`
        },
        "sameAs": []
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}/#webpage`,
        "url": pageUrl,
        "name": pageTitle,
        "isPartOf": {
          "@id": `${pageUrl}/#website`
        },
        "description": pageDescription,
        "inLanguage": "pt-BR"
      },
      {
        "@type": "SoftwareApplication",
        "name": "Upmindy",
        "applicationCategory": "EducationalApplication",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "BRL"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "ratingCount": "1250"
        },
        "operatingSystem": "Web, iOS, Android",
        "description": pageDescription
      }
    ]
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Google One Tap */}
      <GoogleOneTap />
      
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={pageUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Upmindy" />
        <meta property="og:locale" content="pt_BR" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />
        
        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="Portuguese" />
        <meta name="author" content="Upmindy" />
        <meta name="application-name" content="Upmindy" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Navbar />

      {/* Upmindy platform banner - automatic creation, generic */}
      <section className={`relative isolate overflow-hidden px-4 py-16 transition-colors duration-300 sm:px-6 sm:py-20 lg:px-8 lg:py-24 ${isDark ? 'bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900' : 'bg-gradient-to-br from-slate-50 via-violet-50 to-sky-100'}`}>
        <div className={`pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full blur-3xl ${isDark ? 'bg-violet-600/20' : 'bg-violet-400/20'}`} />
        <div className={`pointer-events-none absolute -bottom-40 right-0 h-96 w-96 rounded-full blur-3xl ${isDark ? 'bg-blue-500/15' : 'bg-sky-400/20'}`} />

        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-8 lg:grid-cols-[1fr_0.95fr] lg:gap-x-16 lg:gap-y-6">
          <div className="text-center lg:text-left">
            <div className={`mb-6 inline-flex items-center gap-2 rounded-full border px-4 py-2 shadow-lg ${isDark ? 'border-violet-200/20 bg-violet-200/10 shadow-violet-950/20' : 'border-violet-200 bg-white/80 shadow-violet-200/50'}`}>
              <BoltIcon className={`h-5 w-5 ${isDark ? 'text-amber-300' : 'text-amber-500'}`} />
              <span className={`text-sm font-semibold ${isDark ? 'text-violet-100' : 'text-violet-800'}`}>Tudo criado automaticamente</span>
            </div>

            <h2 className={`mx-auto mb-6 max-w-2xl text-3xl font-extrabold leading-[1.12] tracking-tight sm:text-4xl lg:mx-0 lg:text-5xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Seu material de estudo,{' '}
              <span className={`bg-clip-text text-transparent ${isDark ? 'bg-gradient-to-r from-violet-300 via-pink-300 to-blue-300' : 'bg-gradient-to-r from-violet-700 via-fuchsia-600 to-blue-700'}`}>
                pronto em segundos
              </span>
            </h2>

            <p className={`mx-auto mb-8 max-w-xl text-base leading-relaxed sm:text-lg lg:mx-0 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Flashcards, quizzes, jogos de associação e módulos de estudo são criados para você de forma automática. Você só escolhe o que quer aprender e revisa com repetição espaçada.
            </p>

          </div>

          <div className={`relative rounded-3xl border p-4 backdrop-blur-xl transition-colors duration-300 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1 ${isDark ? 'border-white/10 bg-white/[0.045] shadow-2xl shadow-slate-950/30' : 'border-white/80 bg-white/75 shadow-2xl shadow-slate-300/50'}`}>
            <div className="mb-4 flex items-center justify-between px-1">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-violet-200/70' : 'text-violet-700'}`}>Comece agora</p>
                <h3 className={`mt-1 text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Em apenas 3 passos</h3>
              </div>
              <span className={`rounded-full border px-3 py-1 text-xs font-medium ${isDark ? 'border-white/10 bg-white/5 text-slate-300' : 'border-slate-200 bg-white/80 text-slate-600'}`}>Simples e rápido</span>
            </div>

            <div className="grid grid-cols-1 gap-3 text-left">
              {[
                { step: '1', icon: <AcademicCapIcon className={`h-5 w-5 ${isDark ? 'text-violet-200' : 'text-violet-700'}`} />, title: 'Escolha o assunto', text: 'Defina o que você quer estudar.', tone: isDark ? 'bg-violet-500/20' : 'bg-violet-100' },
                { step: '2', icon: <BoltIcon className={`h-5 w-5 ${isDark ? 'text-amber-200' : 'text-amber-700'}`} />, title: 'Receba tudo pronto', text: 'Flashcards, quizzes, jogos e módulos são gerados automaticamente.', tone: isDark ? 'bg-amber-500/20' : 'bg-amber-100' },
                { step: '3', icon: <RocketLaunchIcon className={`h-5 w-5 ${isDark ? 'text-emerald-200' : 'text-emerald-700'}`} />, title: 'Estude e evolua', text: 'Revise no momento certo com repetição espaçada e acompanhe seu progresso.', tone: isDark ? 'bg-emerald-500/20' : 'bg-emerald-100' }
              ].map((item) => (
                <div key={item.step} className={`group relative flex min-h-[5.5rem] items-center gap-4 rounded-2xl border p-4 transition-all duration-200 ${isDark ? 'border-white/[0.08] bg-slate-950/30 hover:border-violet-300/25 hover:bg-white/[0.07]' : 'border-slate-200/80 bg-white/80 hover:border-violet-300 hover:bg-white'}`}>
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${isDark ? 'ring-1 ring-white/10' : ''} ${item.tone}`}>
                    {item.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className={`mb-0.5 text-sm font-semibold sm:text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.title}</h4>
                    <p className={`text-xs leading-relaxed sm:text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{item.text}</p>
                  </div>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-colors ${isDark ? 'border-white/10 bg-white/[0.04] text-slate-400 group-hover:border-violet-300/30 group-hover:text-violet-200' : 'border-slate-200 bg-slate-50 text-slate-500 group-hover:border-violet-300 group-hover:text-violet-700'}`}>{item.step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col justify-center gap-3 sm:flex-row lg:col-start-1 lg:row-start-2 lg:justify-start">
            <button
              onClick={() => navigate(user ? '/home' : '/register')}
              className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-violet-500 via-fuchsia-500 to-blue-500 px-7 py-3.5 font-semibold text-white shadow-lg shadow-violet-500/25 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-violet-500/30"
            >
              <BoltIcon className="mr-2 h-5 w-5" />
              {user ? 'Criar conteúdo agora' : 'Criar conta grátis'}
            </button>
            <Link
              to="/search"
              className={`inline-flex items-center justify-center rounded-xl border px-7 py-3.5 font-medium transition-all duration-200 ${isDark ? 'border-white/20 bg-white/[0.03] text-white hover:border-white/40 hover:bg-white/10' : 'border-slate-300 bg-white/70 text-slate-700 hover:border-violet-300 hover:bg-white hover:text-violet-800'}`}
            >
              Explorar conteúdos
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Deck & Quiz Exploration Section */}
      <section className={`py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 ${isDark ? 'bg-gray-800' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
              Explore <span className="text-blue-600">Flashcards, Quizzes & Matches Compartilhados</span>
            </h2>
            <p className={`text-lg sm:text-xl max-w-3xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Descubra milhares de flashcards, quizzes e matches interativos criados e avaliados pela comunidade.
              Aprenda idiomas, tecnologia, ciências e muito mais.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Programming Decks */}
            <div className={`${isDark ? 'bg-gray-700' : 'bg-white'} rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105`}>
              <div className="flex items-center mb-4">
                <CpuChipIcon className="h-8 w-8 text-blue-600" />
                <h3 className="text-xl font-semibold ml-3">Tecnologia</h3>
              </div>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
                Programação, informática básica, ferramentas digitais e muito mais. 
                Desenvolva suas habilidades tecnológicas.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-blue-100 text-blue-700'}`}>
                  Programação
                </span>
                <span className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-blue-100 text-blue-700'}`}>
                  Informática
                </span>
                <span className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-blue-100 text-blue-700'}`}>
                  Digital
                </span>
              </div>
            </div>

            {/* Language Decks */}
            <div className={`${isDark ? 'bg-gray-700' : 'bg-white'} rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105`}>
              <div className="flex items-center mb-4">
                <BookOpenIcon className="h-8 w-8 text-green-600" />
                <h3 className="text-xl font-semibold ml-3">Idiomas</h3>
              </div>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
                Inglês, espanhol, vocabulário e gramática.
                Aprenda novos idiomas de forma eficiente.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-green-100 text-green-700'}`}>
                  Inglês
                </span>
                <span className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-green-100 text-green-700'}`}>
                  Espanhol
                </span>
                <span className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-green-100 text-green-700'}`}>
                  Vocabulário
                </span>
              </div>
            </div>

            {/* Academic Subjects */}
            <div className={`${isDark ? 'bg-gray-700' : 'bg-white'} rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105`}>
              <div className="flex items-center mb-4">
                <AcademicCapIcon className="h-8 w-8 text-purple-600" />
                <h3 className="text-xl font-semibold ml-3">Conhecimento Geral</h3>
              </div>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
                História, geografia, ciências e matemática.
                Amplie seu conhecimento em diversas áreas.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-purple-100 text-purple-700'}`}>
                  História
                </span>
                <span className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-purple-100 text-purple-700'}`}>
                  Ciências
                </span>
                <span className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-purple-100 text-purple-700'}`}>
                  Geografia
                </span>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/search"
              className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg shadow-lg hover:shadow-xl"
            >
              <BookOpenIcon className="h-5 w-5 mr-2" />
              Explorar Todos os Flashcards, Quizzes & Matches
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Por que escolher o Upmindy?
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto px-2">
              Nossa plataforma combina ciência cognitiva com tecnologia moderna para 
              criar a melhor experiência de aprendizado em qualquer área do conhecimento
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {platformFeatures.map((feature, index) => (
              <div
                key={index}
                className={`p-4 sm:p-6 rounded-xl transition-all hover:scale-105 hover:shadow-lg ${
                  isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
                } border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              >
                <div className="mb-3 sm:mb-4 flex justify-center">{feature.icon}</div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3 text-center">{feature.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 text-center leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={`py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Transforme sua jornada de aprendizado
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 px-2">
              Metodologia comprovada para acelerar seu aprendizado em qualquer área
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {studyBenefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="mb-4 flex justify-center">{benefit.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              O que nossos estudantes dizem
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Histórias reais de transformação profissional
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl ${
                  isDark ? 'bg-gray-800' : 'bg-white'
                } shadow-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-4 italic">
                  "{testimonial.content}"
                </p>
                <div>
                  <div className="font-semibold">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-20 px-4 sm:px-6 lg:px-8 ${isDark ? 'bg-gradient-to-r from-blue-900 to-purple-900' : 'bg-gradient-to-r from-blue-600 to-purple-600'}`}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Sua jornada de aprendizado começa agora
          </h2>
          <p className="text-xl mb-8">
            Junte-se a milhares de estudantes que já transformaram sua forma de aprender com o Upmindy
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/register"
              className="px-8 py-4 bg-white text-blue-600 rounded-lg text-lg font-medium hover:bg-gray-100 transition-all transform hover:scale-105"
            >
              Criar Conta Gratuita
            </Link>
            <Link
              to="/docs"
              className="px-8 py-4 border-2 border-white text-white rounded-lg text-lg font-medium hover:bg-white hover:text-blue-600 transition-all"
            >
              Ver Como Funciona
            </Link>
          </div>
        </div>
      </section>

      <Footer />

    </div>
  );
};

export default MainLanding;
